import { DeleteOutlined, EditOutlined, UserOutlined } from "@ant-design/icons"
import type { TablePaginationConfig, TableProps } from "antd"
import { Button, Space, Switch, Table, Tag, Tooltip } from "antd"
import type React from "react"

import { TABLE_ACTION_COLUMN_WIDTH } from "@/constants/table"
import { useTableScroll } from "@/hooks/useTableScroll"

import "@/styles/table.full.css"
import { formatTimestamp } from "@/utils/dateUtils"

interface AdminTableProps {
  data: any[]
  loading: boolean
  selectedRowKeys: React.Key[]
  onSelectionChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => void
  onEdit: (record) => void
  onDelete: (record) => void
  onStatusChange: (record, checked: boolean) => void
  pagination?: TableProps["pagination"]
}

const getRoleColor = (role: string): string => {
  const colorMap: Record<string, string> = {
    super_admin: "red",
    admin: "blue",
    editor: "green",
    user: "default",
  }

  return colorMap[role] || "default"
}

/**
 * Component bảng danh sách quản trị viên (AdminTable)
 */
const AdminTable: React.FC<AdminTableProps> = ({
  data,
  loading,
  selectedRowKeys,
  onSelectionChange,
  onEdit,
  onDelete,
  onStatusChange,
  pagination,
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps["columns"] = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 40,
      align: "center",
      render: (value: string) => <span className="font-semibold">{value}</span>,
    },
    {
      title: "Tên đăng nhập",
      dataIndex: "username",
      key: "username",
      width: 240,
      fixed: "left",
      ellipsis: { showTitle: true },
      render: (_: string, record) => (
        <>
          <div className="flex items-center gap-2">
            <UserOutlined className="text-blue-500!" />
            <span>{record?.["username"]}</span>
          </div>
          <p className="text-neutral-600">
            (<i>{record?.["email"]}</i>)
          </p>
        </>
      ),
    },
    {
      title: "Họ và tên",
      dataIndex: "full_name",
      key: "full_name",
      width: 160,
      ellipsis: { showTitle: true },
    },
    {
      title: "Vai trò",
      dataIndex: "role",
      key: "role",
      width: 140,
      align: "center",
      render: (_: any, record: any) => {
        const listRole = record.admin_roles?.map((item: any) => item.role?.name) || []
        return (
          <Space orientation="vertical">
            {listRole.map((role: string, index: number) => (
              <Tag color={getRoleColor(role)} key={index}>
                {role}
              </Tag>
            ))}
          </Space>
        )
      },
    },
    {
      title: "Trạng thái",
      dataIndex: "is_active",
      key: "is_active",
      width: 95,
      align: "center",
      render: (value: number, record) => (
        <Switch
          checked={value === 1}
          onChange={(checked) => onStatusChange(record, checked)}
          checkedChildren="On"
          unCheckedChildren="Off"
        />
      ),
    },
    {
      title: "Ngày tạo",
      dataIndex: "created_at",
      key: "created_at",
      render: (_: any, record: any) => {
        return (
          <Space orientation="vertical" size="small">
            <span>Lần cuối: {formatTimestamp(record.last_login_at, "dd/mm/yy hh:mm") || "_"}</span>
            <span>Ngày tạo: {formatTimestamp(record.created_at, "dd/mm/yy hh:mm")} </span>
            {/* <span>Người tạo: {record.creator?.username}</span> */}
          </Space>
        )
      },
    },
    {
      title: "Thao tác",
      key: "action",
      width: TABLE_ACTION_COLUMN_WIDTH,
      align: "center",
      fixed: "right",
      render: (_: string, record) => (
        <Space size="small" wrap={false}>
          <Tooltip title="Sửa">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined className="text-base! text-blue-500! hover:text-blue-600!" />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>

          <Tooltip title="Xóa">
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined className="text-base! text-red-500! hover:text-red-600!" />}
              onClick={() => onDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  // Cấu hình lựa chọn dòng (row selection)
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectionChange,
  }

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

AdminTable.displayName = "AdminTable"

export default AdminTable
